/**
 * Billing request/response shapes — Phase 3 (M3.3) + Phase M1 (Stripe).
 *
 * Budget limits are stored as `bigint` (micros-USD) in the DB.  JSON cannot
 * represent 64-bit integers losslessly, so all micros fields are serialized
 * as numeric strings on the wire. Frontend converts via `BigInt(str)`.
 */
import { z } from "zod";
import { IsoTimestampSchema } from "./common.js";
/** Tenant budget configuration as returned by GET /api/v1/budgets. */
export const BudgetResponseSchema = z.object({
    monthly_cap_micros: z.string().nullable(),
    per_run_cap_micros: z.string().nullable(),
    alert_thresholds_pct: z.array(z.number().int()),
    hard_stop: z.boolean(),
    updated_at: IsoTimestampSchema,
});
/** PUT /api/v1/budgets request body. */
export const UpdateBudgetRequestSchema = z
    .object({
    monthly_cap_micros: z.string().regex(/^\d+$/).nullable().optional(),
    per_run_cap_micros: z.string().regex(/^\d+$/).nullable().optional(),
    hard_stop: z.boolean().optional(),
})
    .refine((v) => v.monthly_cap_micros !== undefined ||
    v.per_run_cap_micros !== undefined ||
    v.hard_stop !== undefined, { message: "At least one field is required." });
/** GET /api/v1/budgets/usage response. */
export const BudgetUsageResponseSchema = z.object({
    used_micros: z.string(),
    cap_micros: z.string().nullable(),
    period_start: IsoTimestampSchema,
    period_end: IsoTimestampSchema,
});
// ── Stripe subscription schemas (M1.1) ─────────────────────────────────────
export const SubscriptionStatusSchema = z.enum([
    "active",
    "past_due",
    "canceled",
    "trialing",
    "unpaid",
    "incomplete",
    "none",
]);
/** GET /api/v1/billing/subscription */
export const SubscriptionResponseSchema = z.object({
    plan: z.enum(["starter", "growth", "scale", "enterprise"]),
    status: SubscriptionStatusSchema,
    current_period_end: IsoTimestampSchema.nullable(),
    cancel_at_period_end: z.boolean(),
    stripe_customer_id: z.string().nullable(),
});
/** POST /api/v1/billing/checkout request body. */
export const CreateCheckoutSessionRequestSchema = z.object({
    plan: z.enum(["starter", "growth", "scale"]),
    success_url: z.string().url(),
    cancel_url: z.string().url(),
});
/** POST /api/v1/billing/checkout response. */
export const CheckoutSessionResponseSchema = z.object({
    url: z.string().url(),
});
/** POST /api/v1/billing/portal response. */
export const BillingPortalResponseSchema = z.object({
    url: z.string().url(),
});
// ── Top-up packs (M1.5) ────────────────────────────────────────────────────
export const TopUpPackIdSchema = z.enum(["boost", "pro", "power", "mega"]);
/** POST /api/v1/billing/topup request body. */
export const CreateTopUpCheckoutRequestSchema = z.object({
    pack: TopUpPackIdSchema,
    success_url: z.string().url(),
    cancel_url: z.string().url(),
});
/** POST /api/v1/billing/topup response. */
export const CreateTopUpCheckoutResponseSchema = z.object({
    url: z.string().url(),
});
// ── Wallet read (M1.5) ─────────────────────────────────────────────────────
/** GET /api/v1/billing/wallet response. */
export const WalletResponseSchema = z.object({
    /** Wallet balance in credits (top-up credits only; never expires). */
    balance_credits: z.string().regex(/^\d+$/),
    /** Whether auto-refill is opted in. */
    auto_refill_enabled: z.boolean(),
    /** Pack id selected for auto-refill (when enabled). */
    auto_refill_pack_id: TopUpPackIdSchema.nullable(),
    /** When wallet drops below this many credits, auto-refill fires. */
    auto_refill_threshold_credits: z.string().regex(/^\d+$/).nullable(),
    /**
     * Monthly credit allowance for the tenant's plan. `null` for custom-plan
     * (Enterprise) tenants where the allowance is set per-deal elsewhere.
     */
    allowance_total_credits: z.string().regex(/^\d+$/).nullable(),
    /** Allowance credits consumed in the current calendar-month period. */
    allowance_used_credits: z.string().regex(/^\d+$/),
    /**
     * Allowance credits left in the current period (`max(total - used, 0)`).
     * `null` for custom-plan tenants — UI should show "unlimited".
     */
    allowance_remaining_credits: z.string().regex(/^\d+$/).nullable(),
    /** Tenant plan tier the allowance is keyed off. */
    plan: z.enum(["starter", "growth", "scale", "enterprise"]),
    /** Start of the current allowance period (calendar month, UTC). */
    period_start: z.string(),
    /** Start of the next period (when the allowance resets). */
    period_end: z.string(),
});
// ── Auto-refill settings (M1.6) ────────────────────────────────────────────
/**
 * PATCH /api/v1/billing/auto-refill — enable/disable auto-refill or update
 * its pack + threshold. Validation: when enabling, both `auto_refill_pack_id`
 * and `auto_refill_threshold_credits` MUST be set (in this request or
 * already persisted). The handler enforces the cross-field rule.
 */
export const UpdateAutoRefillRequestSchema = z
    .object({
    auto_refill_enabled: z.boolean().optional(),
    auto_refill_pack_id: TopUpPackIdSchema.nullable().optional(),
    auto_refill_threshold_credits: z.string().regex(/^\d+$/).nullable().optional(),
})
    .refine((v) => v.auto_refill_enabled !== undefined ||
    v.auto_refill_pack_id !== undefined ||
    v.auto_refill_threshold_credits !== undefined, { message: "At least one field is required." });
//# sourceMappingURL=billing.js.map